
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { formatDateTime, formatDateTimeToDay } from "../../dates";
import { ResponsiveContainer } from '../ResponsiveContainer';
import DatePicker from "react-datepicker";
import { Session } from './Session';
import { useIsAdmin } from "../../hooks/isAdmin";
import { PATHS } from '../../paths';
import { Link } from "react-router-dom";
import throttle from 'lodash.throttle';

import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../queries/tags';
import { CourseAdmin } from './CourseAdmin';
import { CourseTags } from './CourseTags';

import {
  Radio,
  Dropdown,
  Search,
  Accordion,
  Input,
  Button,
  Dimmer,
  Loader,
  Item,
  Divider,
  Icon,
  Container,
  Header,
  Segment,
  Label,
} from 'semantic-ui-react'

import { useQuery, gql, useMutation} from '@apollo/client';

import { useCallback, useRef, useEffect, useState } from "react";
 import { AttendanceGraph } from "./AttendanceGraph";

const GET_COURSES = gql`
query Courses($search: String, $filters: String) {
  courses(search: $search, filters: $filters) {
      id
      name
      SKU
      overseers {
          id
          name
      }
      courseParticipantCount
    }
  }`;

const GET_ORGANISATION_PERSON_ID_FORMULA = gql`
query Organisation {
    me {
      name
    }
}`;

// These will be different when the next block starts
const CURRENT_BLOCK_TAG_ID = process.env.NODE_ENV === 'development' ? 'tagId:96' : 'tagId:945'

const Courses = () => {
  let navigate = useNavigate();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const [search, setSearch] = useState(urlSearchParams.get('search') || "");
  const [tagIds, setTagIds] = useState<string[]>(urlSearchParams.get('tagIds') ? urlSearchParams.get('tagIds')!.split(',') : [CURRENT_BLOCK_TAG_ID]);
  // need to check for "tagId:96" in the tagIds array
  const [currentBlockOnly, setCurrentBlockOnly] = useState(tagIds.includes(CURRENT_BLOCK_TAG_ID));
  const { data: meData } = useQuery(GET_ORGANISATION_PERSON_ID_FORMULA);
  const [shouldShowSearch, setShouldShowSearch] = useState(urlSearchParams.get('search') !== null || urlSearchParams.get('tagIds') !== null);
  //const { loading: coursesLoading, error: coursesError, data: coursesData, refetch } = useQuery(GET_COURSES);
  const { loading: coursesLoading, error: coursesError, data: coursesData, refetch } = useQuery(GET_COURSES,
    {
      variables: {
        search: search,
        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : ""
      }
  });

  // check if there are more than 6 courses, if so, show the search bar
  useEffect(() => {
    if (coursesData && coursesData.courses && coursesData.courses.length > 6) {
      setShouldShowSearch(true);
    }
  }, [coursesData]);

  const { loading: tagsLoading, error: tagsError, data: tagsData} = useQuery(GET_POSSIBLE_TAGS, {
      variables: {
          showPeopleTags: false,
          showCourseTags: true,
      }
  });

  const setAndHoldTagIds = useCallback((ids: string[]) => {
    if (!ids.includes(CURRENT_BLOCK_TAG_ID)) {
      setCurrentBlockOnly(false);
    }
    setTagIds(ids);
    navigate({ pathname: location.pathname, search: `?search=${search}&tagIds=${ids.join(',')}` });
  },[search, navigate, location.pathname]);

  const timeoutRef = useRef();

  const handleSearchChange = useCallback((_: any, data: any) => {
    clearTimeout(timeoutRef.current)
    setSearch(data.value)

    // @ts-ignore
    timeoutRef.current = setTimeout(() => {
        navigate({ pathname: location.pathname, search: `?search=${data.value}&tagIds=${tagIds.join(',')}` });
        refetch({ search: data.value, filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : "" })
    }, 500);
  }, [tagIds, currentBlockOnly, navigate, location.pathname, refetch]);


  // On path change from broswer back button, we need to update the search state, add a 500ms debounce to the search change handler
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam === null || searchParam === "") {
      // If no search param, reset search state
      setSearch("");
      if (!params.has('tagIds')) {
        // If no tagIds param, reset tagIds state to currentBlockOnly
        setTagIds([CURRENT_BLOCK_TAG_ID]);
        refetch({ search: "", filters: CURRENT_BLOCK_TAG_ID });
      } else {
        // If tagIds param exists, update tagIds state
        const tagIdsParam = params.get('tagIds') || "";
        const newTagIds = tagIdsParam.split(',').filter(id => id !== "");
        setTagIds(newTagIds);
        refetch({ search: "", filters: newTagIds && newTagIds.length > 0 ? newTagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : "" });
      }
    } else {
      // If search param exists, update search state
      setSearch(searchParam);
      const tagIdsParam = params.get('tagIds') || "";
      const newTagIds = tagIdsParam.split(',').filter(id => id !== "");
      setTagIds(newTagIds);
      refetch({ search: searchParam, filters: newTagIds && newTagIds.length > 0 ? newTagIds.join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : "" });
    }

  }, [location.search, refetch, currentBlockOnly]);

  const myCourseCount =  !coursesLoading && !coursesError && coursesData != null && coursesData.courses.length > 0 && coursesData.courses.filter((t:any) => {
                if (meData == null) return false
    return t.overseers != null && t.overseers.map((i:any) => i.name).includes(meData.me.name)
            }).length

  const hasOwnCourses = myCourseCount > 0 && shouldShowSearch && myCourseCount < coursesData.courses.length
  return (
    <ResponsiveContainer>
      <Segment style={{ padding: '0em 0em' }} vertical>
        <Container>
          <Divider horizontal>
            <Header as='h2'>
              Classes
            </Header>
          </Divider>
          { shouldShowSearch && <Search
             loading={coursesLoading}
             input={{ fluid: true }}
             size='large'
             placeholder='Search...'
             open={false}
             onSearchChange={handleSearchChange}
             value={search}
          />}
          {shouldShowSearch && (
            <>
            <br/>
            <Dropdown
              placeholder='Tags'
              fluid
              multiple
              value={tagIds}
              onChange={(_, { value }) => {
                setAndHoldTagIds(value as string[])
                refetch({ search: search, filters: value && (value as string[]).length > 0 ? (value as string[]).join(',') : currentBlockOnly ? CURRENT_BLOCK_TAG_ID : ""})
              }}
              search
              selection
              options={
                tagsLoading || tagsError ? [] :
                (tagsData && tagsData.tags || [])
                  .map((p: any) => ({
                    key: p.id,
                    text: p.name,
                    value: `tagId:${p.id}`,
                  }))
              }
            /></>)}
          {shouldShowSearch && (
            <>
            <br/>
            {/* ON be default */}
           <Radio
             toggle
             label='Current block courses only'
              checked={currentBlockOnly}
              onChange={(_, data:any) => {
                // if you toggle this then add the current block tag id to the tagIds if it is not already there
                setCurrentBlockOnly(data.checked)
                const filters = data.checked ? [...tagIds, CURRENT_BLOCK_TAG_ID] : [...tagIds.filter((t:any) => t !== CURRENT_BLOCK_TAG_ID)]
                setAndHoldTagIds(filters)
                refetch({ search: search, filters: filters && filters.length > 0 ? filters.join(',') : "" })
              }}
           />
            </>
          )}
          {hasOwnCourses && (<>
            <Divider horizontal>
            My courses
          </Divider>
          <Item.Group divided>
            { !coursesLoading && !coursesError && coursesData != null && coursesData.courses.length > 0 && coursesData.courses.filter((t:any) => {
                if (meData == null) return false
                // TODO: change this for new block
    return t.overseers != null && t.overseers.map((i:any) => i.name).includes(meData.me.name) && t.SKU.endsWith('JUL2025')
            }).map((t:any) => {

            return (
              <Item style={{cursor: 'pointer'}} key={t.id} onClick={() => navigate(`/courses/${t.id}`)}>
                <Item.Content>
                  <Item.Header>{t.name}</Item.Header>
                  <Item.Meta>{t.SKU}</Item.Meta>
                  <Icon name='group'/><b>Students:</b> {t.courseParticipantCount} | <Icon name='user'/> <b>Teachers:</b> {t && t.overseers.map((o: any) => (o.name)).join(', ')}
                </Item.Content>
              </Item>
            )
          })}
          </Item.Group>
          <Divider horizontal>
            All courses
          </Divider>
          </>)}
          <Item.Group divided>
          { !coursesLoading && !coursesError && coursesData != null && coursesData.courses.length > 0 ? coursesData.courses.map((t:any) => (
            <Item style={{cursor: 'pointer'}} key={t.id} onClick={() => navigate(`/courses/${t.id}`)}>
              <Item.Content>
                <Item.Header>{t.name}</Item.Header>
                <Item.Meta>{t.SKU}</Item.Meta>
                <Icon name='group'/><b>Students:</b> {t.courseParticipantCount} | <Icon name='user'/> <b>Teachers:</b> {t && t.overseers.map((o: any) => (o.name)).join(', ')}
              </Item.Content>
            </Item>
          )) : (
            <>
              {new Array(20).fill(1).map((_) => (<br/>))}
            </>
          )}
          <>
            {new Array(10).fill(1).map((_) => (<br/>))}
          </>
          </Item.Group>
        </Container>
      </Segment>

    </ResponsiveContainer>
  );
}

const GET_COURSE = gql`
  query Courses($courseId: Int!) {
    course(courseId: $courseId) {
      id
      name
      canEdit
      SKU
      attendancePercentage
      overseers {
          id
          name
      }
      courseParticipantCount
      tags {
        id
        name
      }
      participants {
          id
          name
      }
      sessions {
        id
        course {
            id
            name
            SKU
        }
        durationMinutes
        attendance {
            details
            status
            id
            user {
                name
                id
                identifier
                photoUrl
            }
        }
        startTime
        attendanceCount
        reasonForCancellation
        attendancePercentage
      }
    }
  }`;

const ADD_PERSON_TO_SESSION = gql`
mutation AddPersonToSession($sessionId: Int!, $identifier: String, $status: String) {
  addPersonToSession(sessionId: $sessionId, identifier: $identifier, status: $status) {
    id
  }
}`;

const DELETE_SESSION = gql`
mutation Mutation($sessionId: Int!, $reason: String) {
  cancelSession(sessionId: $sessionId, reason: $reason) {
    id
  }
}`;

const ADD_SESSION_TO_COURSE = gql`
mutation AddSession($courseId: Int!, $date: String) {
  addSession(courseId: $courseId, date: $date) {
    id
  }
}
`;

const UPDATE_SESSION_TIME = gql`
mutation UpdateSessionTime($sessionId: Int!, $magnitude: Int!, $forwardDirection: Boolean!, $unit: String!) {
  updateSessionTime(sessionId: $sessionId, magnitude: $magnitude, forwardDirection: $forwardDirection, unit: $unit) {
    id
  }
}`;

const DUPLICATE_COURSE = gql`
  mutation Mutation($courseToCopyId: Int!, $newCourseName: String!, $daysToCopyItForward: Int!, $newBlockTagId: Int!, $oldBlockTagId: Int!) {
    duplicateCourse(courseToCopyId: $courseToCopyId, newCourseName: $newCourseName, daysToCopyItForward: $daysToCopyItForward, newBlockTagId: $newBlockTagId, oldBlockTagId: $oldBlockTagId) {
      status
    }
  }`;

const DELETE_COURSE = gql`
  mutation Mutation($courseId: Int!) {
    deleteCourse(courseId: $courseId) {
      status
    }
  }`;

const RENAME_COURSE = gql`
  mutation RenameCourse($courseId: Int!, $newCourseName: String!) {
    renameCourse(courseId: $courseId, newCourseName: $newCourseName) {
      status
    }
  }`


const CANCELLATION_REASON = [
  {
    key: 'Teacher-sick',
    text: 'Teacher sick',
    value:'Teacher sick',
  },
  {
    key: 'Teacher-holiday',
    text: 'Teacher holiday',
    value:'Teacher holiday',
  },
  {
    key: 'Company-cancelled',
    text: 'Company cancelled',
    value:'Company cancelled',
  },
  {
    key: 'Other',
    text: 'Other',
    value:'Other',
  }
]

const Course = () => {
  let navigate = useNavigate();
  let { id } = useParams();
  const isAdmin = useIsAdmin();

  const [disableDuplicate, setDisableDuplicate] = useState(false);
  const [newCourseNameDuplicate, setNewCourseNameDuplicate] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [daysToCopyItForward, setDaysToCopyItForward] = useState(0);
  const { loading, error, data, refetch } = useQuery(GET_COURSE, { variables: { courseId: Number(id) } });

  const [addPersonToSession] = useMutation(ADD_PERSON_TO_SESSION);
  const [deleteSession] = useMutation(DELETE_SESSION);

  const [newSessionDate, setNewSessionDate] = useState<Date | null>(new Date());
  const [addSessionToCourse] = useMutation(ADD_SESSION_TO_COURSE);
  const [updateSessionTime] = useMutation(UPDATE_SESSION_TIME);
  const [duplicateClass] = useMutation(DUPLICATE_COURSE);
  const [renameCourse] = useMutation(RENAME_COURSE);
  const [deleteCourse] = useMutation(DELETE_COURSE);

  const [editMode, setEditMode] = useState(false);
  const [activeId, setActiveId] = useState();
  const [identifier, setIdentifier] = useState('');
  const [hasUsedAccordian, setHasUsedAccordian] = useState(false);

  const TenHoursAgo = Number(new Date()) - 10*60*60*1000;

  const updateCourseName = useCallback(throttle(async (newName: string) => {
      renameCourse({ variables: { courseId: Number(id), newCourseName: newName } })
      refetch();
  }, 500, { leading: false, trailing: true }), [renameCourse]);

  useEffect(() => {
    if (!activeId && !loading && !error && data != null && data.course) {
      const nextSession = data.course.sessions.find(
        (s: {startTime:number, reasonForCancellation: string | null}) =>
              s.startTime > TenHoursAgo
          && (s.reasonForCancellation == null || s.reasonForCancellation === ""))

      if (nextSession != null && hasUsedAccordian === false) {
        setActiveId(nextSession.id)
      }
    }
  }, [loading, error, data, activeId, hasUsedAccordian])

  if (loading) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
            <Dimmer active inverted>
              <Loader inverted content='Loading' />
            </Dimmer>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (!data || !data.course) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                Course not found
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (error) {
    return <div />
  }

  // TODO: move Info section to its own component
  return (
    <ResponsiveContainer>
      <Segment key={id} style={{ padding: '2em 0em' }} vertical>
        <Container>
          <Icon name='arrow left' size='large' style={{cursor: 'pointer'}} onClick={() => navigate(-1)} />

          {editMode && isAdmin ? (
            <>
              {/* rename input */}
              <Input placeholder='New course name' value={newCourseName} onChange={async (e: any) => {
                  setNewCourseName(e.target.value)
                  await updateCourseName(e.target.value)
              }} />
            </>
          ) : (
            <span style={{fontSize:"2em", lineHeight: '1.2', verticalAlign: 'middle'}}>{!loading && !error && data && data.course && data.course.name}</span>
          )}

            {isAdmin && (<>
              <br/>
              <Button
                onClick={() => {
                  setEditMode(!editMode)
                  setNewCourseName(data && data.course && data.course.name)
                }}
              >
                Turn edit mode {editMode ? "off" : "on"}
              </Button>
               {editMode && (
                 <>
              <Button
                primary
                onClick={async () => {
                  if (window.confirm("Really delete this? (Use cancel to back out)")) {
                    await deleteCourse({ variables: { courseId: Number(id) }});
                    navigate(-1)
                  } else {
                    console.log('Ok we are backing out go')
                  }
                }}
              >
                Delete
              </Button>
                   <br/>
                   <Header as='h3'>Duplicate class for next block</Header>
                   <Input placeholder='New course name' value={newCourseNameDuplicate} onChange={(e: any) => setNewCourseNameDuplicate(e.target.value)} />
                   <Label pointing='left'>New course name</Label>
                   <br/>
                   <Input placeholder='Days to copy it forward' value={daysToCopyItForward} onChange={(e: any) => {
                     const value = Number(e.target.value)
                     if (value < 0 || isNaN(value)) {
                       return
                     }
                     setDaysToCopyItForward(Number(e.target.value))
                   }} />
                   <Label pointing='left'>Number of days into the future the duplicate should be</Label>
                   {/* put the first session start time forward by the days to copy it forward*/}
                   (first class: {data && data.course && data.course.sessions.length > 0 && formatDateTime(Number(data.course.sessions[0].startTime) + daysToCopyItForward *24*60*60*1000)})
                   <br/>
                   <Button
                      disabled={disableDuplicate || newCourseNameDuplicate === '' || daysToCopyItForward === 0}
                      onClick={async () => {
                        await duplicateClass({ variables: {
                          courseToCopyId: Number(id),
                          newCourseName: newCourseNameDuplicate,
                          daysToCopyItForward,
                          newBlockTagId: 945,
                          oldBlockTagId: 731,
                        }});
                        setDisableDuplicate(true)
                      }}
                    >
                      Duplicate Class for next block
                  </Button>
                 </>)}
              </>
            )}
          <Divider horizontal>
            <Header as='h4'>
              Info
            </Header>
          </Divider>
          <AttendanceGraph data={data} />
          <b>SKU:</b> {data && data.course && data.course.SKU}
          <br />
          <b>Teachers:</b> {data && data.course && data.course.overseers.map((o: any) => (
              <Link to={`${PATHS.PEOPLE}/${o.id}`}>
                  {o.name}
              </Link>
            )).reduce((prev: any, curr: any) => [prev, ', ', curr], [])}
          <br />
          <b>Attendance:</b> {data && data.course && Math.round(data.course.attendancePercentage * 100)}%
          <br />
          <b>Participants:</b> {data && data.course && data.course.courseParticipantCount}
          <br />
          <b>Tags:</b>
          <CourseTags tags={data && data.course && data.course.tags} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
          {editMode && <CourseAdmin participants={(data && data.course && data.course.participants) || []} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />}
          <Divider horizontal>
            <Header as='h4'>
              Sessions
            </Header>
          </Divider>
          {loading && (<Dimmer active inverted>
              <Loader inverted content='Loading' />
          </Dimmer>)
          }
          <Accordion>
            {!loading && !error && data != null && data.course && data.course.sessions.map((s: any) => {

              if (!editMode && s.reasonForCancellation != null && s.reasonForCancellation === 'Other') {
                return null; // Skip sessions that are cancelled with 'Other' reason
              }

              const inFuture = s.startTime > Number(new Date())

              return (
              <>
                <Accordion.Title
                  key={s.id+'title'}
                  style={s.startTime > TenHoursAgo ? {}: { opacity: "0.51"}}
                  active={activeId === s.id}
                  index={0}
                  onClick={() => {
                    if (s.id !== activeId) {
                      setIdentifier('');
                      setActiveId(s.id)
                    } else {
                      setActiveId(undefined)
                    }
                    setHasUsedAccordian(true)
                  }}
                >
                { s.reasonForCancellation != null ? (<b>Class cancelled on {formatDateTimeToDay(s.startTime)}</b>) : (<>
                  <Icon name='dropdown' />
                  <Icon name='user'/> {s.attendanceCount} | {" "}
                  <b>
                    {formatDateTime(s.startTime)}
                  </b> {!inFuture ? `(attendance: ${Math.round(s.attendancePercentage*100)}%)`: ""}
                  {isAdmin && editMode && (<>
                    <Button
                      secondary
                      onClick={async (e:any) => {
                        e.stopPropagation()
                          await deleteSession({ variables: { sessionId: s.id, reason: 'Teacher sick'} })
                        refetch({ variables: { courseId: Number(id) } })
                      }}
                    >
                      Cancel session
                    </Button>
                    <Button
                      onClick={async (e:any) => {
                        e.stopPropagation()
                        await updateSessionTime({ variables: {
                          sessionId: s.id,
                          magnitude: 15,
                          forwardDirection: true,
                          unit: "minutes",
                        }});
                        refetch({ variables: { courseId: Number(id) } })
                      }}
                    >
                      +15m
                    </Button>
                    <Button
                      onClick={async (e:any) => {
                        e.stopPropagation()
                        await updateSessionTime({ variables: {
                          sessionId: s.id,
                          magnitude: 1,
                          forwardDirection: true,
                          unit: "hours",
                        }});
                        refetch({ variables: { courseId: Number(id) } })
                      }}
                    >
                      +1h
                    </Button>
                    <Button
                      onClick={async (e:any) => {
                        e.stopPropagation()
                        await updateSessionTime({ variables: {
                          sessionId: s.id,
                          magnitude: 1,
                          forwardDirection: false,
                          unit: "hours",
                        }});
                        refetch({ variables: { courseId: Number(id) } })
                      }}
                    >
                      -1h
                    </Button>
                    <Button
                      onClick={async (e:any) => {
                        e.stopPropagation()
                        await updateSessionTime({ variables: {
                          sessionId: s.id,
                          magnitude: 1,
                          forwardDirection: true,
                          unit: "days",
                        }});
                        refetch({ variables: { courseId: Number(id) } })
                      }}
                    >
                      +1d
                    </Button>
                    <Button
                      onClick={async (e: any) => {
                        e.stopPropagation()
                        await updateSessionTime({ variables: {
                          sessionId: s.id,
                          magnitude: 1,
                          forwardDirection: false,
                          unit: "days",
                        }});
                        refetch({ variables: { courseId: Number(id) } })
                      }}
                    >
                      -1d
                    </Button>
                  </>)}
              </>)}
                </Accordion.Title>
                {s.reasonForCancellation != null ? (<>
                  {isAdmin && editMode ? (
                    <Dropdown
                        placeholder='Reason...'
                        selection
                        value={s.reasonForCancellation}
                        onChange={async (_, d) =>  {
                          await deleteSession({ variables: { sessionId: s.id, reason: d.value} })
                          setTimeout(() => refetch({ courseId: Number(id) }), 2000)
                        }}
                        options={CANCELLATION_REASON}
                    />
                  )
                 : (<>Reason: {s.reasonForCancellation} </>)}
                  </>
                ) : <Accordion.Content
                  key={s.id}
                  active={activeId === s.id}
                >
                  <Session canEditCourse={data && data.course && data.course.canEdit} session={s as any} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
                  {isAdmin && editMode && (
                    <>
                    <Input
                        placeholder='StudentId to add'
                        value={identifier.toLocaleUpperCase()}
                        onKeyPress={async (e: any) => {
                           if (e.key === 'Enter') {
                              await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                              refetch({ variables: { courseId: Number(id) } })
                              setIdentifier('')
                           }
                        }}
                        onChange={(e: any) => setIdentifier(e.target.value)}
                    />
                    <Button
                      style={{marginLeft: '0.25em'}}
                      onClick={async () => {
                        await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                        refetch({ variables: { courseId: Number(id) } })
                        setIdentifier('')
                      }}
                    >Add</Button>
                    <Button
                      onClick={async () => {
                        await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PRESENT' } })
                        refetch({ variables: { courseId: Number(id) } })
                        setIdentifier('')
                      }}
                    >Add as Present</Button>
                    </>
                  )}
                </Accordion.Content>
                }
              </>
            )
            })
          }
          </Accordion>
            {isAdmin && editMode && (
              <>
              {/* Add a date picker */}

              <DatePicker
                  selected={newSessionDate}
                  dateFormat="dd/MM/yyyy"
                  onChange={(date) => {
                      if (date == null) return;
                      setNewSessionDate(date);
                  }}
                  showYearDropdown
                  maxDate={new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())}
              />

              <br/>
              <Button
                onClick={async () => {
                  await addSessionToCourse({ variables: { courseId: Number(id), date: newSessionDate?.toISOString() } })
                  // move new session date forward 7 days
                  if (newSessionDate == null) return;
                  const newDate = new Date(newSessionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                  setNewSessionDate(newDate);
                  refetch({ variables: { courseId: Number(id) } })
                }}
              >
                Add session
              </Button>
              </>
            )}
        </Container>
      </Segment>

    </ResponsiveContainer>
  );
}

export { Course, Courses }
